<template>
    <div class="event-overview">
        <div class="event-overview__header">
            <div class="event-overview__header-inner">
                <div class="event-overview__heading">
                    <div class="event-overview__title-row">
                        <span class="event-overview__back" @click="$router.push({ path: '/brand/events/index', query: { tab: tabFor } })">←</span>
                        <h2 class="event-overview__title">{{ event ? event.title : '' }}</h2>
                        <EventStatusPill v-if="event" :status="event.status"></EventStatusPill>
                    </div>
                    <span v-if="event && event.organizerName" class="event-overview__by">{{ langJson('by') }} {{ event.organizerName }}</span>
                </div>
                <EventActions v-if="event" :event="event" @changed="onChanged"></EventActions>
            </div>
        </div>

        <div v-if="event" class="event-overview__body">
            <div v-if="event.status === 'rejected' && event.rejectionReason" class="event-overview__notice event-overview__notice--danger">
                <strong>{{ langJson('Changes requested') }}</strong>
                <span>{{ event.rejectionReason }}</span>
            </div>

            <div v-if="event.deployStatus === 'deploying'" class="event-overview__notice event-overview__notice--info">
                <strong>{{ langJson('Launching your event…') }}</strong>
                <span>{{ langJson('We’re creating your ticket contract on-chain. This usually takes about a minute — you can leave this page.') }}</span>
            </div>
            <div v-else-if="event.deployStatus === 'failed'" class="event-overview__notice event-overview__notice--danger">
                <strong>{{ langJson('Launch didn’t finish') }}</strong>
                <span>{{ langJson('Nothing went on sale. Try Launch again — if it keeps failing, contact support with this detail:') }} {{ event.deployError }}</span>
            </div>
            <div v-else-if="event.status === 'approved' && !(event.stripe && event.stripe.chargesEnabled)" class="event-overview__notice event-overview__notice--warn">
                <strong>{{ langJson('Connect Stripe to launch') }}</strong>
                <span>{{ langJson('Your event is approved. Finish Stripe setup so buyers can pay, then launch.') }}</span>
            </div>

            <template v-if="isLiveOrEnded">
                <div class="event-overview__stats">
                    <EventStatCard icon="🎟" :label="langJson('Tickets sold')" :value="stats ? stats.ticketsSold.toLocaleString() : '—'" :sub="stats ? `${langJson('of')} ${stats.ticketsTotal.toLocaleString()}` : ''"></EventStatCard>
                    <EventStatCard icon="👥" :label="langJson('Buyers')" :value="stats ? stats.buyers.toLocaleString() : '—'"></EventStatCard>
                    <EventStatCard icon="⛽" :label="langJson('Total gas fee')" :value="stats ? `${formatGas(stats.gas.amount)} ${stats.gas.symbol}` : '—'" :sub="langJson('Paid by NFTWerks')"></EventStatCard>
                    <EventStatCard icon="💰" :label="langJson('Income')" :value="stats ? money(stats.grossIncome) : '—'" :sub="stats ? `${money(stats.netIncome)} ${langJson('after platform fee')}` : ''"></EventStatCard>
                </div>
                <div v-if="stats && stats.tiers.length" class="event-overview__tiers">
                    <div v-for="tier in stats.tiers" :key="tier.id" class="event-overview__tier">
                        <span>{{ tier.name }}</span>
                        <div class="event-overview__bar"><div :style="{ width: `${tier.total ? Math.min(100, (tier.sold / tier.total) * 100) : 0}%` }"></div></div>
                        <span class="event-overview__tier-count">{{ tier.sold }} / {{ tier.total }}</span>
                    </div>
                </div>
            </template>

            <h3 class="event-overview__section">{{ langJson('EVENT OVERVIEW') }}</h3>
            <div class="event-overview__list">
                <div v-for="item in isLiveOrEnded ? [...setupSteps, ...afterApproval] : setupSteps" :key="item.step" class="event-overview__row" @click="goStep(item.step)">
                    <GI hs="56" ws="56" :name="item.icon"></GI>
                    <div class="event-overview__row-text">
                        <h4>{{ langJson(item.title) }}</h4>
                        <span>{{ langJson(item.subtitle) }}</span>
                    </div>
                    <span v-if="item.done" class="event-overview__tick">✓</span>
                </div>
            </div>

            <template v-if="!isLiveOrEnded">
            <p class="event-overview__hint">{{ langJson(review.hint) }}</p>
            <div :class="['event-overview__review', `event-overview__review--${event.status}`]">
                <GI hs="56" ws="56" name="project_review"></GI>
                <div class="event-overview__row-text">
                    <h4>{{ langJson(review.title) }}</h4>
                    <span>{{ langJson(review.body) }}</span>
                    <span class="event-overview__accent">{{ review.accent }}</span>
                </div>
            </div>

            <div class="event-overview__divider"></div>

            <h3 class="event-overview__section">{{ langJson('AFTER APPROVAL') }}</h3>
            <div :class="['event-overview__list', !approved && 'event-overview__list--locked']">
                <div v-for="item in afterApproval" :key="item.step" class="event-overview__row" @click="approved && goStep(item.step)">
                    <GI hs="56" ws="56" :name="item.icon"></GI>
                    <div class="event-overview__row-text">
                        <h4>{{ langJson(item.title) }}</h4>
                        <span>{{ langJson(item.subtitle) }}</span>
                    </div>
                </div>
            </div>
            </template>
        </div>
        <div v-else-if="error" class="event-overview__body event-overview__error">{{ error }}</div>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { STATUS_TAB, apiErrorMessage } from '@/assets/js/organizerEvent'

export default {
    name: 'EventOverviewPage',
    data() {
        return {
            event: null,
            stats: null,
            error: '',
        }
    },
    computed: {
        tabFor() {
            return this.event ? STATUS_TAB[this.event.status] : 'draft'
        },
        isLiveOrEnded() {
            return this.event && ['live', 'ended'].includes(this.event.status)
        },
        approved() {
            return this.event && ['approved', 'live', 'ended'].includes(this.event.status)
        },
        setupSteps() {
            const checklist = (this.event && this.event.checklist) || {}
            return [
                { step: 'basics', icon: 'edit_basics', title: 'Basics', subtitle: 'Name your event, upload cover, and establish your event details', done: checklist.basics },
                { step: 'stripe', icon: 'edit_treaty_active', title: 'Connect with Stripe', subtitle: 'Set up payment', done: checklist.stripe },
                { step: 'ticketing', icon: 'edit_rewards', title: 'Ticket Details', subtitle: 'Specify ticket tiers and prices', done: checklist.ticketing },
            ]
        },
        review() {
            const submitted = this.event.submittedAt
                ? `${this.langJson('Submitted')} ${new Date(this.event.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                : ''
            return (
                {
                    in_review: { hint: 'Your event is being reviewed', title: 'In review', body: 'We’re checking it follows our rules and guidelines — we’ll email you the outcome.', accent: submitted || this.langJson('Please allow 1 business day for a response.') },
                    approved: { hint: 'Your event is approved', title: 'Approved', body: 'Set up promotion and ticket verification, then launch to open ticket sales.', accent: '' },
                    rejected: { hint: 'Make the requested changes and submit again', title: 'Changes requested', body: this.event.rejectionReason || 'See the note above.', accent: '' },
                    live: { hint: 'Your event is live', title: 'On sale', body: 'Tickets are on sale on your event page.', accent: '' },
                    ended: { hint: 'Your event has ended', title: 'Ended', body: 'Ticket sales are closed.', accent: '' },
                }[this.event.status] || {
                    hint: 'Submit your event for review',
                    title: 'Project review',
                    body: 'We’ll check to make sure it follows our rules and guidelines.',
                    accent: this.langJson('Please allow 1 business day for a response.'),
                }
            )
        },
        afterApproval() {
            return [
                { step: 'promotion', icon: 'edit_promotion_', title: 'Promotion', subtitle: 'Generate your ticketing URL and invite participants' },
                { step: 'verification', icon: 'edit_links', title: 'Ticket Verification', subtitle: 'Set up ticket verification' },
            ]
        },
    },
    created() {
        this.$store.commit('saveHeadView', { create: false })
        this.load()
    },
    methods: {
        async load() {
            try {
                const res = await organizerApi.getEvent(this.$route.query.id)
                this.event = res.data
                this.loadStats()
            } catch (err) {
                this.error = apiErrorMessage(err, this.langJson('Could not load this event'))
            }
        },
        async loadStats() {
            if (!this.isLiveOrEnded) return
            try {
                const res = await organizerApi.getEventStats(this.event.id)
                this.stats = res.data
            } catch (err) {
                this.stats = null
            }
        },
        onChanged(event) {
            this.event = event
            this.loadStats()
        },
        money(amount) {
            const currency = (this.stats && this.stats.currency) || 'USD'
            try {
                return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount)
            } catch (e) {
                return `${currency} ${amount.toFixed(2)}`
            }
        },
        formatGas(amount) {
            return amount === 0 ? '0' : amount < 0.0001 ? '<0.0001' : amount.toLocaleString('en', { maximumFractionDigits: 4 })
        },
        goStep(step) {
            this.$router.push({ path: '/brand/events/edit', query: { id: this.event.id, step } })
        },
    },
}
</script>
<style lang="scss" scoped>
.event-overview {
    padding-bottom: 64px;

    &__header {
        border-bottom: 1px solid var(--border_color);
    }
    &__header-inner {
        max-width: var(--max_width);
        margin: 0 auto;
        min-height: 144px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
    }
    &__title-row {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    &__back {
        font-size: 28px;
        cursor: pointer;
        color: var(--font_deep_color);
    }
    &__title {
        font-family: 'Cabinet Grotesk';
        font-weight: 800;
        font-size: 40px;
        line-height: 48px;
        color: var(--font_deep_color);
    }
    &__by {
        font-family: 'Lato';
        font-size: 16px;
        color: var(--font_deep_color);
    }
    &__body {
        max-width: var(--max_width);
        margin: 0 auto;
    }
    &__notice {
        margin-top: 24px;
        padding: 16px 24px;
        border-radius: 24px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-family: 'Lato';
        font-size: 14px;

        &--danger {
            background: #fde8e8;
            color: #b42318;
        }
        &--info {
            background: #f1e9ff;
            color: #6d28d9;
        }
        &--warn {
            background: #fff6e5;
            color: #b54708;
        }
    }
    &__stats {
        margin-top: 24px;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 24px;
    }
    &__tiers {
        margin-top: 16px;
        background: var(--color_white);
        border-radius: 24px;
        padding: 16px 24px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        font-family: 'Lato';
        font-size: 14px;
        color: var(--font_deep_color);
    }
    &__tier {
        display: grid;
        grid-template-columns: 160px 1fr 90px;
        align-items: center;
        gap: 16px;
    }
    &__tier-count {
        text-align: right;
        color: var(--font_color);
    }
    &__bar {
        height: 8px;
        border-radius: 8px;
        background: var(--bg_color);
        overflow: hidden;

        div {
            height: 100%;
            background: linear-gradient(90deg, #e879b2 0%, #a855f7 100%);
        }
    }
    &__section {
        margin: 24px 0 16px;
        font-family: 'Inter';
        font-weight: 400;
        font-size: 18px;
        line-height: 28px;
        color: var(--font_deep_color);
    }
    &__list {
        background: var(--color_white);
        border-radius: 32px;
        overflow: hidden;

        &--locked {
            opacity: 0.5;

            .event-overview__row {
                cursor: not-allowed;
            }
        }
    }
    &__row,
    &__review {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px 24px;
        box-sizing: border-box;
    }
    &__row {
        cursor: pointer;
        border-bottom: 1px solid var(--bg_color);

        &:last-child {
            border-bottom: none;
        }
        &:hover {
            background: var(--bg_color);
        }
    }
    &__row-text {
        flex: 1;
        display: flex;
        flex-direction: column;

        h4 {
            font-family: 'Lato';
            font-weight: 700;
            font-size: 16px;
            color: var(--font_color_black);
        }
        span {
            font-family: 'Lato';
            font-size: 14px;
            color: var(--font_color);
        }
    }
    &__accent {
        color: var(--primary_color, var(--main_color)) !important;
    }
    &__tick {
        color: #12a150;
        font-weight: 700;
        font-size: 18px;
    }
    &__hint {
        margin: 24px 0;
        text-align: center;
        font-family: 'Lato';
        font-size: 14px;
        color: var(--font_modif_color);
    }
    &__review {
        border: 1px solid var(--border_color);
        border-radius: 32px;

        &--in_review {
            border-color: #f5c56b;
            background: #fffbf2;
        }
        &--approved,
        &--live {
            border-color: #8fdcae;
            background: #f3fcf7;
        }
    }
    &__divider {
        margin: 40px 0 0;
        border-bottom: 1px solid var(--border_color);
    }
    &__error {
        padding: 64px 0;
        text-align: center;
        font-family: 'Lato';
        color: var(--font_modif_color);
    }
}

@media only screen and (max-width: 1080px) {
    .event-overview {
        &__header-inner,
        &__body {
            padding: 0 16px;
            box-sizing: border-box;
        }
        &__header-inner {
            min-height: 96px;
            padding-top: 16px;
            padding-bottom: 16px;
            flex-direction: column;
            align-items: stretch;
        }
        &__heading {
            min-width: 0;
        }
        &__title-row {
            gap: 12px;
        }
        &__title {
            flex: 1;
            min-width: 0;
            overflow-wrap: anywhere;
            font-size: 28px;
            line-height: 34px;
        }
        &__list,
        &__review {
            border-radius: 24px;
        }
        &__stats {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
        }
        &__tier {
            grid-template-columns: 1fr 1fr;

            .event-overview__bar {
                grid-column: 1 / -1;
                grid-row: 2;
            }
        }
    }
}
</style>
